import { SectionTitle, SectionTitleSizes } from '@components/common/section-title';
import React from 'react';
import { useI18NextContext } from '@components/hooks';
import { CMSDestination, TripTypes } from '@model/contentful';
import { MiniExperience } from '@model/contentful/experience/experience';
import { TourDetails } from '@model/tours';
import styled from '@emotion/styled';
import { Grid } from '@mui/material';
import { getImageUrl } from '@util/images';
import { DestinationLinkBuilder, LinkItem } from '@model/iceberg';
import { Page } from '@model/common';
import { breakpoints } from '@styles/breakpoints';
import { ExperienceProductTile } from '@components/product/experiences/experience-product-tile';
import { Theme, useTheme } from '@emotion/react';
import { CtaButton, ButtonColor, ButtonSize, ButtonVariant } from '@components/material-ui';
import Link from 'next/link';
import { TestId } from '@components/test-ids';

export interface ExperiencesProps {
  destination: CMSDestination;
  experiences: Array<MiniExperience> | null;
  tripType: TripTypes;
  className?: string;
}

type MiniTourDetails = Pick<TourDetails, 'name' | 'price' | 'image' | 'path' | 'days'>;

const Container = styled.div({});

function getGridSize(count: number): any {
  switch (count) {
    case 1:
      return {
        xs: 12
      };
    case 2:
      return {
        xs: 12,
        sm: 6
      };
    default:
      return {
        xs: 12,
        sm: 6,
        md: 4
      };
  }
}

function getTileImageSize(count: number): number {
  switch (count) {
    case 1:
      return breakpoints.large;
    case 2:
      return breakpoints.medium;
    default:
      return breakpoints.small;
  }
}

export const Experiences = ({ experiences, destination, tripType, className }: ExperiencesProps) => {
  const t: any = useI18NextContext();
  const theme: Theme = useTheme();
  const title: string = destination.title;
  const destinationPath: string = destination.destinationPath.fields.path;
  if (tripType !== TripTypes.HOLIDAYS || !experiences?.length) return null;
  const miniTourDetails: Array<MiniTourDetails> = experiences.map((tour: MiniExperience) => ({
    days: tour.days,
    image: tour.hero.fields.file.url,
    name: tour.title,
    path: tour.path,
    price: tour.price
  }));
  const total: number = miniTourDetails.length;
  const renderTours = () =>
    miniTourDetails.slice(0, 3).map(({ name, price, image, path, days }: MiniTourDetails) => {
      const builder: DestinationLinkBuilder = new DestinationLinkBuilder();
      const link: LinkItem = builder.getGuideLink(destinationPath, path, Page.EXPERIENCES, TripTypes.HOLIDAYS);
      return (
        <Grid key={name} item {...getGridSize(total)}>
          <ExperienceProductTile
            name={name}
            imageUrl={getImageUrl(image, getTileImageSize(total))}
            price={price}
            link={link}
            days={days}
          />
        </Grid>
      );
    });
  const viewAllLink: LinkItem = new DestinationLinkBuilder().getGuidesLink(
    destinationPath,
    Page.EXPERIENCES,
    TripTypes.HOLIDAYS
  );
  return (
    <Container className={className} data-testid={TestId.experienceProductTile.group}>
      <SectionTitle
        size={SectionTitleSizes.LARGE}
        heading={t('destination__mini-tours--title', { destination: title })}
        main
      />
      <Grid container spacing={theme!.custom.gridSpacing.default}>
        {renderTours()}
      </Grid>
      {experiences.length > 3 && (
        <Link href={viewAllLink.href} as={viewAllLink.as}>
          <CtaButton
            label={t('experiences__cta--label', { destination: destination.title })}
            color={ButtonColor.SECONDARY}
            variant={ButtonVariant.OUTLINED}
            size={ButtonSize.REGULAR}
            testId={TestId.experienceProductTile.cta}
          />
        </Link>
      )}
    </Container>
  );
};
